import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GitMerge, Users, Envelope, Phone, TextT, Warning, CheckCircle, ArrowRight, Sparkle } from "@phosphor-icons/react";

const REASON_META = {
  email: { label: "Même email", icon: Envelope, color: "#EF4444" },
  phone: { label: "Même téléphone", icon: Phone, color: "#F59E0B" },
  name: { label: "Nom similaire", icon: TextT, color: "#7CB2FF" },
};

function ClientCard({ client, isPrimary, onPickPrimary }) {
  return (
    <div
      className={`rounded-xl border p-3 flex-1 min-w-0 transition-colors cursor-pointer ${
        isPrimary
          ? "bg-emerald-500/10 border-emerald-500/50"
          : "bg-white/[0.02] border-white/10 hover:border-white/30"
      }`}
      onClick={onPickPrimary}
      data-testid={`dup-client-${client.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate" title={client.name}>{client.name}</div>
          {client.email && <div className="text-[11px] text-white/60 truncate">{client.email}</div>}
          {client.phone && <div className="text-[11px] text-white/60">{client.phone}</div>}
          {(client.address || client.city) && (
            <div className="text-[10px] text-white/40 mt-1 truncate">
              {[client.address, client.postal_code, client.city].filter(Boolean).join(" · ")}
            </div>
          )}
          {client.notes && (
            <div className="text-[9px] text-white/30 mt-1 truncate italic">{client.notes}</div>
          )}
        </div>
        {isPrimary && (
          <div className="shrink-0 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
            <CheckCircle size={10} weight="fill" /> Gardé
          </div>
        )}
      </div>
    </div>
  );
}

function DuplicateGroup({ group, onMerged }) {
  const [primaryId, setPrimaryId] = useState(group.clients[0].id);
  const [merging, setMerging] = useState(false);
  const meta = REASON_META[group.reason] || REASON_META.name;
  const Icon = meta.icon;

  const merge = async () => {
    const secondary = group.clients.find((c) => c.id !== primaryId);
    if (!secondary) return;
    setMerging(true);
    try {
      await api.post("/admin/clients/merge", { primary_id: primaryId, secondary_id: secondary.id });
      toast.success(`Fusionné dans « ${group.clients.find((c) => c.id === primaryId)?.name} »`);
      onMerged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMerging(false);
    }
  };

  // Only support 2-by-2 merges — if a group has 3+, the user merges pairs sequentially
  const pair = group.clients.slice(0, 2);
  const primary = pair.find((c) => c.id === primaryId) || pair[0];
  const secondary = pair.find((c) => c.id !== primaryId) || pair[1];

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`dup-group-${group.key}`}>
      <div className="px-4 py-2 flex items-center gap-2 border-b border-white/5 bg-white/[0.02]">
        <Icon size={14} weight="duotone" style={{ color: meta.color }} />
        <span className="text-xs font-medium">{meta.label}</span>
        <span className="text-[10px] text-white/40 font-mono truncate max-w-xs">{group.key}</span>
        {group.count > 2 && (
          <span className="text-[9px] uppercase tracking-widest bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">
            +{group.count - 2} de plus
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">
          Clique sur celui à conserver puis fusionner
        </div>
        <div className="flex flex-col md:flex-row items-stretch gap-3">
          <ClientCard client={primary} isPrimary={true} onPickPrimary={() => setPrimaryId(primary.id)} />
          <div className="flex items-center justify-center text-white/30">
            <ArrowRight size={18} weight="bold" />
          </div>
          <ClientCard client={secondary} isPrimary={false} onPickPrimary={() => setPrimaryId(secondary.id)} />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            onClick={merge}
            disabled={merging}
            className="h-9 bg-[#005CFF] hover:bg-[#0047CC] text-xs"
            data-testid={`dup-merge-${group.key}`}
          >
            <GitMerge size={14} weight="bold" /> {merging ? "Fusion en cours…" : "Fusionner"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDuplicatesPanel() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batching, setBatching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/clients/duplicates");
      setGroups(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mergeAll = async () => {
    if (!window.confirm(`Fusionner automatiquement les ${groups.length} groupes ? Le client le plus complet sera gardé pour chaque paire.`)) return;
    setBatching(true);
    try {
      const r = await api.post("/admin/clients/merge-all");
      toast.success(`${r.data.merged} paires fusionnées`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBatching(false);
    }
  };

  if (loading || groups === null) {
    return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;
  }

  return (
    <div className="space-y-4" data-testid="client-duplicates-panel">
      <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Users size={20} weight="duotone" className="text-amber-400" />
              <div className="font-display font-semibold">Doublons clients détectés</div>
            </div>
            <div className="text-[11px] text-white/60 leading-relaxed">
              Détection auto par email, téléphone et nom normalisé (sans accents ni ponctuation).
              <br />
              <span className="text-white/40">
                Sélectionne le client à conserver, puis &quot;Fusionner&quot; — tous les chantiers, notifs et historique
                du secondaire sont transférés vers le principal avant suppression.
              </span>
            </div>
          </div>
          {groups.length > 0 && (
            <Button
              onClick={mergeAll}
              disabled={batching}
              className="shrink-0 h-9 bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs"
              data-testid="dup-merge-all-btn"
            >
              <Sparkle size={14} weight="fill" /> {batching ? "Fusion…" : `Fusionner tout (${groups.length})`}
            </Button>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-2xl bg-emerald-500/5 border border-emerald-500/20 p-6 text-center" data-testid="dup-empty">
          <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-400 mb-2" />
          <div className="text-sm font-medium">Aucun doublon détecté</div>
          <div className="text-[11px] text-white/50 mt-1">Ta base clients est propre.</div>
        </div>
      ) : (
        <>
          <div className="text-[11px] text-white/50 flex items-center gap-1">
            <Warning size={12} weight="fill" className="text-amber-400" />
            {groups.length} groupe{groups.length > 1 ? "s" : ""} de doublons trouvé{groups.length > 1 ? "s" : ""}
          </div>
          {groups.map((g) => (
            <DuplicateGroup key={g.key} group={g} onMerged={load} />
          ))}
        </>
      )}
    </div>
  );
}
